import type {
  AudioGuideCallbacks,
  AudioGuidePlaybackStatus,
} from "@/types/audio-guide"

import { audioBlobFromBase64 } from "@/lib/audio-utils"

type OpenAiAudioGuide = {
  audio_base64: string
  content_type: string
  transcript: string
}

const MAX_CACHE_ENTRIES = 10

// Plays and caches the complete audio returned by the OpenAI guide endpoint.
export class OpenAIAudioGuideEngine {
  private static sharedCache = new Map<string, OpenAiAudioGuide>()

  private audio: HTMLAudioElement | null = null
  private objectUrl: string | null = null
  private callbacks: AudioGuideCallbacks = {}
  private status: AudioGuidePlaybackStatus = "idle"

  private static cacheSet(key: string, value: OpenAiAudioGuide) {
    OpenAIAudioGuideEngine.sharedCache.delete(key)
    OpenAIAudioGuideEngine.sharedCache.set(key, value)
    if (OpenAIAudioGuideEngine.sharedCache.size > MAX_CACHE_ENTRIES) {
      const oldest = OpenAIAudioGuideEngine.sharedCache.keys().next().value
      if (oldest !== undefined) {
        OpenAIAudioGuideEngine.sharedCache.delete(oldest)
      }
    }
  }

  private setStatus(status: AudioGuidePlaybackStatus) {
    this.status = status
    this.callbacks.onStatusChange?.(status)
  }

  private cleanupAudio() {
    if (this.audio) {
      this.audio.pause()
      this.audio.removeAttribute("src")
      this.audio = null
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }

  stop() {
    this.cleanupAudio()
    this.status = "idle"
  }

  pause() {
    if (!this.audio || this.status !== "playing") return
    this.audio.pause()
    this.setStatus("paused")
    this.notifyProgress()
  }

  resume() {
    if (!this.audio || this.status !== "paused") return
    void this.audio.play().catch(() => {
      this.setStatus("error")
      this.callbacks.onError?.("Unable to play audio guide.")
    })
  }

  hasBufferedAudio(cacheKey: string): boolean {
    return OpenAIAudioGuideEngine.sharedCache.has(cacheKey)
  }

  async playBufferedAudio(cacheKey: string, callbacks?: AudioGuideCallbacks) {
    const guide = OpenAIAudioGuideEngine.sharedCache.get(cacheKey)
    if (!guide) return false

    await this.play(guide, callbacks)
    return true
  }

  async start(
    guide: OpenAiAudioGuide,
    cacheKey: string,
    callbacks?: AudioGuideCallbacks
  ) {
    OpenAIAudioGuideEngine.cacheSet(cacheKey, guide)
    await this.play(guide, callbacks)
  }

  private async play(guide: OpenAiAudioGuide, callbacks?: AudioGuideCallbacks) {
    this.stop()
    this.callbacks = callbacks ?? {}
    this.callbacks.onTranscript?.(guide.transcript)
    this.setStatus("loading")

    const audio = new Audio()
    audio.preload = "auto"
    audio.setAttribute("playsinline", "")
    this.audio = audio
    this.objectUrl = URL.createObjectURL(
      audioBlobFromBase64(guide.audio_base64, guide.content_type)
    )
    audio.src = this.objectUrl
    audio.onloadedmetadata = () => this.notifyProgress()
    audio.ontimeupdate = () => this.notifyProgress()
    audio.onplay = () => this.setStatus("playing")
    audio.onended = () => {
      if (this.audio !== audio) return
      this.cleanupAudio()
      this.status = "idle"
      this.callbacks.onEnded?.()
    }
    audio.onerror = () => {
      if (this.audio !== audio) return
      this.cleanupAudio()
      this.setStatus("error")
      this.callbacks.onError?.("Unable to play audio guide.")
    }

    try {
      await audio.play()
    } catch {
      if (this.audio === audio) {
        this.cleanupAudio()
        this.setStatus("error")
        this.callbacks.onError?.("Unable to play audio guide.")
      }
    }
  }

  private notifyProgress() {
    if (!this.audio) return
    this.callbacks.onProgress?.({
      elapsed: this.audio.currentTime,
      duration: Number.isFinite(this.audio.duration) ? this.audio.duration : 0,
    })
  }
}
